import React, { useEffect, useMemo, useRef } from "react";
import { withRouter } from "react-router-dom";
import { withTranslation } from "react-i18next";
import ReactSVG from "react-svg";
import cx from "classnames";
import Actions from "./Actions";
import i18n from 'util/i18n';
import get from 'lodash/get';

import "./styles.scss";

function getTextWidth(element) {
    const range = document.createRange();
    range.selectNodeContents(element);
    const width = range.getBoundingClientRect().width;
    range.detach();
    return width;
}


const Breadcrumbs = props => {
    const {
        t,
        className,
        breadcrumbs,
        setBreadCrumbs,
        currentId, // either orderId or crumbId
        customerActions,
        contactPersonActions,
        mainActions,
        history,
        // withCollapseButton,
        collapseState,
        onCollapseClick,
        location: { pathname: currentPath },
        match: {
            params: { subTab, id, crumbId },
        },
    } = props;

    const breadcrumbsRef = useRef(null);

    const statusCodeColors = useMemo(() => {
        return get(i18n.getDataByLanguage(i18n?.language), "dictionary.specInstStatesColor", {});
    }, []);

    const renderBreadcrumbs = (data, index) => {
        const { type, id, statusClassName, path, locked, name, statusCode, statusName, subTab, code, icon } = data;
        const toPath = subTab ? `${path}/${subTab}` : path;

        const withCustomerActions = customerActions && type === "customer";
        const withContactPersonActions = contactPersonActions && type === "contactPerson";
        const withMainActions = mainActions && !["customer", "contactPerson"].includes(type);
        const withActions = withMainActions || withContactPersonActions || withCustomerActions;

        const nextCrumb = breadcrumbs[index + 1];
        let isNextActive = false;
        if (nextCrumb && +nextCrumb.id === +currentId) {
            isNextActive = true;
        }

        const prevCrumb = breadcrumbs[index - 1];
        let isPrevActive = false;
        if (prevCrumb && +prevCrumb.id === +currentId) {
            isPrevActive = true;
        }

        const statusCodeColor = statusCodeColors[statusCode];
        
        return (
            <div
                className={cx("breadcrumb", {
                    active: +currentId === +id,
                    "is-next-active": isNextActive,
                    "is-prev-active": isPrevActive,
                    [`crumb-type-${type}`]: type,
                })}
                key={id}
            >
                <div className="breadcrumb-link">
                {/* <div className={cx("breadcrumb-link", { [`crumb-type-${type}`]: type, })}> */}
                    <div
                        className={cx("breadcrumb-link-wrapper", {
                            "with-actions": withActions,
                            // [`crumb-type-${type}`]: type,
                        })}
                        onClick={() => currentPath !== toPath && history.push(toPath)}
                    >
                        {icon && <ReactSVG className="tab-svg-wrapper" svgClassName="tab-svg" src={`/data/svg/${icon}.svg`} />}
                        {name && (
                            <span class="code-name" title={code ? `${code} ${name}` : name}>
                                {code && <span className="code">{code}</span>}
                                <span className="name">{name}</span>
                            </span>
                        )}

                        {locked && <i className="icon icon-lock" />}

                        {type === "crumb" && <i className={`icon icon-circle ${statusCode}`} title={statusName} />}

                        {type === "main" && (
                            <div title={statusName}
                                className={`status breadcrumb-status ${statusCode} ${statusClassName || ""}` }
                                style={statusCodeColor && { backgroundColor: statusCodeColor }}
                            >
                                <span>{statusName}</span>
                            </div>
                        )}
                    </div>
                    {withCustomerActions && <Actions breadcrumbData={data} actions={customerActions} /> }
                    {withContactPersonActions && <Actions breadcrumbData={data} actions={contactPersonActions} />}
                    {withMainActions && (
                        <Actions breadcrumbData={data} actions={mainActions} />
                    )}
                </div>
            </div>
        );
    };

    useEffect(() => {
        // id === customer Id - do not update breadcrumbs from customer tab
        if (breadcrumbs && subTab && !id) {
            // do not update false breadcrumbs (when id are not synced)
            const isMainIncluded = breadcrumbs.find(item => item.type === "main" && +item.id === +currentId);

            // main is not here and no crumbId as well - no additional checks required
            if (!isMainIncluded && !crumbId) {
                return;
            }
            if (crumbId) {
                const isCrumbIncluded = breadcrumbs.find(item => item.type === "crumb" && +item.id === +crumbId);
                if (!isCrumbIncluded) {
                    return;
                }
            }
            // eithe main or crumb is there - sync breadcrumbs
            const breadcrumbsData = breadcrumbs.map(crumb => (+crumb.id === +currentId ? { ...crumb, subTab } : crumb));
            setBreadCrumbs(breadcrumbsData);
        }
    }, [subTab]);

    useEffect(() => {
        if (!breadcrumbsRef.current) return;

        const updateBreadcrumbsSize = () => {
            const container = breadcrumbsRef && breadcrumbsRef.current;
            if (!container) {
                return;
            }
    
    
            const customerContainer = container.querySelector(".breadcrumb.crumb-type-customer");
            const mainContainer = container.querySelector(".breadcrumb.crumb-type-main");
            const codeName = container.querySelector(".breadcrumb.crumb-type-main .code-name");
    
            const code = container.querySelector(".breadcrumb.crumb-type-main .code-name .code");
            const name = container.querySelector(".breadcrumb.crumb-type-main .code-name .name");
            const status = container.querySelector(".breadcrumb.crumb-type-main .status.breadcrumb-status")
            // reset width of customer container if no main container available
            if (customerContainer && !mainContainer) {
                customerContainer.style.width = "";
                customerContainer.style.minWidth = "";
            }
            if (!customerContainer || !mainContainer || !codeName || !code || !name) {
                return;
            }

            // order lock icon
            let lockIconWidth = 0;
            const lockIcon = container.querySelector(".breadcrumb.crumb-type-main .icon.icon-lock");
            if (lockIcon) {
                const styles = window.getComputedStyle(lockIcon);
                const marginLeft = styles ? parseInt(styles.marginLeft, 10) : 0;
                const marginRight = styles ? parseInt(styles.marginRight, 10) : 0;
                const width = lockIcon.getBoundingClientRect().width;
                lockIconWidth = Math.floor(width + marginLeft + marginRight);
            }
    
            // reset layout before each run
            codeName.style.width = "";
            codeName.style.minWidth = "";
            mainContainer.style.width = "";
            mainContainer.style.minWidth = "";
            customerContainer.style.width = "";
            customerContainer.style.minWidth = "";
            name.classList.remove("hidden");

            const leftIconWidth = 16;
            // const customerPadding = 16;
            const totalWidth = container.getBoundingClientRect().width;
            const customerWidth = customerContainer.getBoundingClientRect().width;
            // const customerFullWidth = getTextWidth(customerContainer) + leftIconWidth + customerPadding;
            // const customerCroppedWidth = customerFullWidth > customerWidth ? customerFullWidth - customerWidth : 0;

            // 1️ total available space for the main container
            const availableForMain = totalWidth - customerWidth;

            const xPadding = 32;
            const rightIconWidth = 20;
            const statusExtraPadding = 8;
            // const extraWidth = 7;  // some extra width to bypass ellipsis overflow + right margin 3px
            const extraWidth = 6;  // some extra width to bypass ellipsis overflow + right margin 3px
            const rightMargin = 3;
            const codeWidth = Math.floor(getTextWidth(code) + extraWidth); // we should keep code in full width
            const statusWidth = Math.floor(getTextWidth(status) + statusExtraPadding); // we should keep status in full width

            // 2️ minimum required width to render code+lock+status only (hide name)
            const minMainWidth = codeWidth + leftIconWidth + xPadding + statusWidth + rightIconWidth + lockIconWidth;

            const nameWidth = Math.floor(getTextWidth(name) + rightMargin);
            // 3 max required width to render code + name + lock + status (everything)
            const maxMainWidth = minMainWidth + nameWidth + extraWidth + lockIconWidth;

            // if there is available space to render all - render all
            if (availableForMain >= maxMainWidth) {
                // enough space for everything: code + name + status
                mainContainer.style.width = maxMainWidth + "px";
                mainContainer.style.minWidth = maxMainWidth + "px";

                customerContainer.style.width = `calc(100% - ${maxMainWidth}px)`;
                customerContainer.style.minWidth = `calc(100% - ${maxMainWidth}px)`;

                codeName.style.width = (codeWidth + nameWidth) + "px"; // code + name
                codeName.style.minWidth = (codeWidth + nameWidth) + "px";

            } else {
                // no space available for everything - hide name
                // enough space for code + status and maybe for part of name
                // calculate possible available width for the name
                // existing fallback to show the name partially (looks like it is not needed at all)
                // let visibleNameWidth = 0;
                // if (availableForMain < minMainWidth) {
                //     name.classList.add("hidden");
                // } else {
                //     // calculate but make sure that customer is not cropped by extracting customerCroppedWidth
                //     const calculatedNameWidth = availableForMain - minMainWidth - extraWidth - customerCroppedWidth;
                //     if (calculatedNameWidth > 0) {
                //         visibleNameWidth = calculatedNameWidth;
                //         name.classList.remove("hidden");
                //     } else {
                //         name.classList.add("hidden");
                //     }
                // }
                // mainContainer.style.width = minMainWidth + visibleNameWidth + "px";
                // mainContainer.style.minWidth = minMainWidth + visibleNameWidth + "px";

                // customerContainer.style.width = `calc(100% - ${minMainWidth + visibleNameWidth}px)`;
                // customerContainer.style.minWidth = `calc(100% - ${minMainWidth + visibleNameWidth}px)`;

                // codeName.style.width = codeWidth + visibleNameWidth + "px"; // code only
                // codeName.style.minWidth = codeWidth + visibleNameWidth + "px";
                name.classList.add("hidden");
                mainContainer.style.width = minMainWidth + "px";
                mainContainer.style.minWidth = minMainWidth + "px";

                customerContainer.style.width = `calc(100% - ${minMainWidth}px)`;
                customerContainer.style.minWidth = `calc(100% - ${minMainWidth}px)`;

                codeName.style.width = codeWidth + "px";
                codeName.style.minWidth = codeWidth + "px";
            }
        };
    
        const resizeObserver = new ResizeObserver(updateBreadcrumbsSize);
    
        resizeObserver.observe(breadcrumbsRef.current);

        updateBreadcrumbsSize();

        return () => resizeObserver.disconnect();
        
    }, [breadcrumbs, collapseState]);

    return (
        <div
            ref={breadcrumbsRef}
            className={cx("breadcrumbs-ordering-container", {
                [className]: className,
                "with-collapse": collapseState && onCollapseClick,
            })}
        >
            {collapseState && onCollapseClick ? (
                <div className="button-hover-half-circle button-collapse-state" onClick={onCollapseClick}>
                    <ReactSVG
                        className="button-icon button-icon-size"
                        src={`/data/svg/tab-left-${collapseState}.svg`}
                    />
                </div>
            ) : (<div className="empty-collapse-placeholder"></div>)}
            <div
                className={cx("breadcrumbs breadcrumbs-ordering", {
                    // [className]: className,
                    // "with-collapse": collapseState && onCollapseClick,
                })}
            >
                {breadcrumbs && breadcrumbs.map(renderBreadcrumbs)}
            </div>
        </div>
    );
};

export default withTranslation()(withRouter(Breadcrumbs));
